import { useSettingsStore } from '../stores/useSettingsStore';

/**
 * useFormatters hook - provides unit-aware formatting functions.
 */
export function useFormatters() {
  const units = useSettingsStore((s) => s.units);
  const isMetric = units === 'metric';

  /** Format speed from m/s to display units */
  const formatSpeed = (ms) => {
    if (ms == null) return '0';
    const value = isMetric ? ms * 3.6 : ms * 2.237;
    return Math.round(value).toString();
  };

  /** Speed unit label */
  const speedUnit = isMetric ? 'km/h' : 'mph';

  /** Format distance from meters to display units */
  const formatDistance = (m) => {
    if (m == null) return '0';
    if (isMetric) {
      return m >= 1000 ? (m / 1000).toFixed(1) : Math.round(m).toString();
    }
    const miles = m * 0.000621371;
    return miles >= 1 ? miles.toFixed(1) : (m * 3.28084).toFixed(0);
  };

  /** Distance unit label */
  const distanceUnit = (m) => {
    if (isMetric) return m >= 1000 ? 'km' : 'm';
    return m * 0.000621371 >= 1 ? 'mi' : 'ft';
  };

  /** Format vertical from meters */
  const formatVertical = (m) => {
    if (m == null) return '0';
    return isMetric ? Math.round(m).toString() : Math.round(m * 3.28084).toString();
  };

  /** Vertical unit */
  const verticalUnit = isMetric ? 'm' : 'ft';

  /** Format altitude from meters */
  const formatAltitude = (m) => {
    if (m == null) return '0';
    return isMetric ? Math.round(m).toString() : Math.round(m * 3.28084).toString();
  };

  /** Altitude unit */
  const altitudeUnit = isMetric ? 'm' : 'ft';

  /** Format temperature from Celsius */
  const formatTemp = (c) => {
    if (c == null) return '--';
    return isMetric ? `${Math.round(c)}°C` : `${Math.round(c * 1.8 + 32)}°F`;
  };

  /** Format duration from ms to HH:MM:SS */
  const formatDuration = (ms) => {
    if (ms == null || ms <= 0) return '0:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  /** Format duration from ms to short form like "2h 15m" */
  const formatDurationShort = (ms) => {
    if (ms == null || ms <= 0) return '0m';
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return {
    formatSpeed,
    speedUnit,
    formatDistance,
    distanceUnit,
    formatVertical,
    verticalUnit,
    formatAltitude,
    altitudeUnit,
    formatTemp,
    formatDuration,
    formatDurationShort,
    isMetric,
  };
}
